<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue";

/**
 * Props:
 * - v-model: string|number|null
 * - options: Array<string|number|{label:string, value:any}>
 * - placeholder?: string
 * - disabled?: boolean
 * - dedupeByLabel?: boolean | default true
 * - filterMode?: 'startsWith' | 'includes'  default 'startsWith'
 * - allowFreeText?: boolean | default false
 * - selectOnExactMatch?: boolean | default true
 *
 */
const props = defineProps({
  modelValue: { type: [String, Number, null], default: null },
  options: { type: Array, default: () => [] },
  placeholder: { type: String, default: "Please select" },
  disabled: { type: Boolean, default: false },
  dedupeByLabel: { type: Boolean, default: true },
  filterMode: { type: String, default: "startsWith" },

  allowFreeText: { type: Boolean, default: false },

  selectOnExactMatch: { type: Boolean, default: true },
});

const emit = defineEmits(["update:modelValue", "change"]);

const open = ref(false);
const query = ref("");
const hi = ref(-1);

const norm = (opt) =>
  typeof opt === "object" && opt !== null
    ? { label: String(opt.label ?? ""), value: opt.value }
    : { label: String(opt ?? ""), value: opt };

const valuesMatch = (a, b) => {
  if (a === b) return true;
  if (a == null || b == null) return false;
  return String(a) === String(b);
};

const flat = computed(() => {
  const arr = (props.options || []).map(norm);
  if (!props.dedupeByLabel) return arr;
  const seen = new Set();
  return arr.filter((o) => {
    const key = o.label.trim().toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
});

const findOptionByValue = (value) =>
  flat.value.find((option) => valuesMatch(option.value, value)) ?? null;

const syncQueryFromModel = () => {
  if (props.modelValue == null || props.modelValue === "") {
    query.value = "";
    return;
  }

  const match = findOptionByValue(props.modelValue);
  if (match) {
    if (query.value !== match.label) {
      query.value = match.label;
    }
    return;
  }

  const raw = String(props.modelValue);
  if (query.value !== raw) {
    query.value = raw;
  }
};

watch(
  () => [props.modelValue, props.options],
  () => {
    syncQueryFromModel();
  },
  { immediate: true, deep: true }
);

const normText = (s) => String(s ?? "").trim().toLowerCase();

watch(query, (v) => {
  const typed = normText(v);
  if (!typed) {
    if (props.allowFreeText) {
      if (props.modelValue !== '' && props.modelValue !== null) {
        emit("update:modelValue", '');
        emit("change", '');
      }

      return;
    }

    if (props.modelValue != null && props.modelValue !== '') {
      emit("update:modelValue", null);
      emit("change", null);
    }

    return;
  }

  const match = flat.value.find((o) => normText(o.label) === typed);

  if (match) {
    if (!props.selectOnExactMatch) {
      if (props.allowFreeText) {
        if (props.modelValue !== v) {
          emit("update:modelValue", v);
          emit("change", v);
        }
      }

      return;
    }

    if (!valuesMatch(props.modelValue, match.value)) {
      emit("update:modelValue", match.value);
      emit("change", match.value);
    }

    if (query.value !== match.label) {
      query.value = match.label;
    }

    return;
  }

  if (props.allowFreeText) {
    if (props.modelValue !== v) {
      emit("update:modelValue", v);
      emit("change", v);
    }
    return;
  }

  if (props.modelValue == null) {
    return;
  }
});

const filtered = computed(() => {
  const q = query.value.trim().toLowerCase();
  if (!q) return flat.value;
  return flat.value.filter((o) => {
    const lbl = o.label.toLowerCase();
    return props.filterMode === "includes" ? lbl.includes(q) : lbl.startsWith(q);
  });
});

function openDd() {
  if (props.disabled) return;
  open.value = true;
  hi.value = -1;
}
function closeDd() {
  open.value = false;
  hi.value = -1;
}

function select(o) {
  emit("update:modelValue", o.value);
  emit("change", o.value);
  query.value = o.label;
  closeDd();
}

function onKey(e) {
  if (props.disabled) return;

  if (!open.value && (e.key === "ArrowDown" || e.key === "Enter")) {
    e.preventDefault();
    openDd();
    return;
  }
  if (!open.value) return;

  const max = filtered.value.length - 1;
  if (e.key === "ArrowDown") {
    e.preventDefault();
    hi.value = hi.value < max ? hi.value + 1 : 0;
  } else if (e.key === "ArrowUp") {
    e.preventDefault();
    hi.value = hi.value > 0 ? hi.value - 1 : max;
  } else if (e.key === "Enter") {
    e.preventDefault();
    if (hi.value >= 0) select(filtered.value[hi.value]);
  } else if (e.key === "Escape") {
    e.preventDefault();
    closeDd();
  }
}

function onDocClick(ev) {
  if (!ev.target.closest?.("[data-ss-root]")) closeDd();
}
onMounted(() => document.addEventListener("click", onDocClick));
onBeforeUnmount(() => document.removeEventListener("click", onDocClick));

function clearSelection() {
  select({ label: "", value: null });
}
</script>

<template>
  <div data-ss-root class="relative">
    <div class="relative mt-1">
      <input
        type="text"
        v-model="query"
        :placeholder="modelValue ? '' : placeholder"
        :disabled="disabled"
        autocomplete="off"
        @focus="openDd"
        @click="openDd"
        @keydown="onKey"
        class="border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-lg shadow-sm block w-full py-2 px-3 pr-10"
      />
      <span class="pointer-events-none absolute inset-y-0 right-3 flex items-center">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-600" viewBox="0 0 20 20" fill="currentColor">
          <path
            fill-rule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
            clip-rule="evenodd"
          />
        </svg>
      </span>
    </div>

    <ul
      v-show="open"
      class="absolute z-20 mt-1 w-full max-h-60 overflow-auto rounded-md border border-gray-200 bg-white shadow-lg"
      role="listbox"
    >
      <template v-if="filtered.length">
        <li
          v-for="(o, idx) in filtered"
          :key="`${o.label}__${String(o.value ?? o.label)}`"
          :class="['px-3 py-2 cursor-pointer select-none', idx === hi ? 'bg-indigo-50' : '']"
          @mouseenter="hi = idx"
          @mouseleave="hi = -1"
          @mousedown.prevent="select(o)"
          role="option"
        >
          {{ o.label }}
        </li>
      </template>
      <template v-else>
        <li class="px-3 py-2 text-gray-500">No results</li>
      </template>

      <li
        v-if="modelValue != null"
        class="px-3 py-2 text-sm text-gray-600 cursor-pointer border-t border-gray-100"
        @mousedown.prevent="clearSelection"
      >
        Clear selection
      </li>
    </ul>
  </div>
</template>